import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { buildCustomerLoginUrl } from "../../utils/customerLink";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Clock, Mail, Download } from "lucide-react";
import PaymentStatusBadge from "../shared/PaymentStatusBadge";
import OrderStageBadge from "../shared/OrderStageBadge";
import EmailOrderDialog from "./EmailOrderDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const STATUS_OPTS = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled"];
// The stage pill comes from shared/OrderStageBadge. The local stage table that used to
// live here fell back to stage 0 for an unmatched status, so a declined order read
// "Payment failed" + "Order Placed" — an order that was never placed.

// Same one-line address rendering the family sees on the customer site
// (customer-infinity/InfinityOrderList.jsx), so both panels read identically.
const addrLine = (a) =>
  a ? [a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ") : "";

const FINGER = { "01": "Left Baby", "02": "Left Ring", "03": "Left Middle", "04": "Left Index", "05": "Left Thumb", "06": "Right Thumb", "07": "Right Index", "08": "Right Middle", "09": "Right Ring", "10": "Right Baby" };
const describeTarget = (t) => (!t ? "Print" : t.finger ? `Finger · ${FINGER[t.finger] || t.finger}` : t.target || t.kind || "Print");

/**
 * Case-centric Infinity Touch order detail. A memorial case → many orders; this
 * shows the case + customer link, then one section per order with its status
 * timeline + inline status change. Auto-scrolls to `focusOrderId` when opened.
 */
export default function InfinityCaseDetail({ open, onClose, memorialRecordId, focusOrderId, apiBase = "admin", readOnly = false, allowEmailOrders = true }) {
  const [record, setRecord] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // One dialog serves both modes; `order` null means "create a new email order".
  const [emailOrderDialog, setEmailOrderDialog] = useState({ open: false, order: null });
  // Site name/tagline/description/logo for the printed document's branded header —
  // same public CMS content already used for the infinitytouch.app tab title/favicon
  // (App.js's applyInfinityTouchBranding), fetched once per open rather than per print.
  const [branding, setBranding] = useState(null);
  // Whether the CMS's logo image failed to load (e.g. a 404'd upload) — the printed
  // header falls back to the site name as text only when this is true, not always.
  const [logoFailed, setLogoFailed] = useState(false);
  const sectionRefs = useRef({});

  const load = useCallback(async () => {
    if (!memorialRecordId) return;
    setLoading(true);
    try {
      const [recRes, ordRes] = await Promise.all([
        axios.get(`${API}/${apiBase}/memorial-records/${memorialRecordId}`).catch(() => ({ data: null })),
        axios.get(`${API}/${apiBase}/orders`, { params: { memorial_record_id: memorialRecordId, limit: 100 } }),
      ]);
      setRecord(recRes.data);
      // Admin orders endpoint returns { orders: [...] }; reseller returns a bare array.
      setOrders(Array.isArray(ordRes.data) ? ordRes.data : (ordRes.data?.orders || []));
    } catch (e) {
      toast.error("Failed to load case");
    } finally {
      setLoading(false);
    }
  }, [memorialRecordId, apiBase]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    setLogoFailed(false);
    axios.get(`${API}/website/content`).then(({ data }) => setBranding(data)).catch(() => setBranding(null));
  }, [open]);

  // Auto-scroll (1.4) to the focused order once loaded.
  useEffect(() => {
    if (!loading && focusOrderId && sectionRefs.current[focusOrderId]) {
      setTimeout(() => sectionRefs.current[focusOrderId]?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [loading, focusOrderId, orders]);

  const s = record?.subject || {};
  // The PIN is case-level (every order carries a copy), but not every order necessarily
  // has one — pick the first that does so the link survives a newer order without it.
  // With no orders at all, fall back to the case's own code: the family must be able to
  // order as soon as the funeral home saves the case, which is the whole point of
  // case_code (TR-25 item 1). Order references stay preferred so links already in the
  // wild and the ones staff have copied before keep matching what is shown here.
  const linkOrder = orders.find((o) => o.customer_pin) || orders[0];
  const linkRef = linkOrder?.order_number || linkOrder?.order_id || record?.case_code;
  const linkPin = linkOrder?.customer_pin || record?.customer_pin;
  const link = buildCustomerLoginUrl(linkRef, linkPin);

  // "Email Ordering" records an order that reached us by email/phone instead of through
  // the app or the customer portal. Offered on EVERY case, including ones that already
  // carry orders — a family can phone in a second order against a case they have already
  // ordered from, and staff still need somewhere to log it. A case can therefore hold
  // SEVERAL email orders; each is moved along from its own card below, not from this
  // button, which now only ever opens a new one.
  // Admin-only: this popup is also rendered by the funeral-home panel with readOnly, and by
  // the ops Orders page with allowEmailOrders false (ops may change order status only).
  const emailOrders = orders.filter((o) => o.is_email_order);
  const canEmailOrder = !readOnly && allowEmailOrders;

  // seo.title is "Infinity Touch – Memories beyond a lifetime" — split into a name/
  // tagline pair for the printed banner's two lines. Falls back gracefully if the
  // CMS content hasn't loaded yet or has no dash in the title.
  const [brandName, brandTagline] = (branding?.seo?.title || "Infinity Touch").split(/\s+[–—-]\s+/);
  const brandLogoUrl = branding?.site?.logo_url
    ? (branding.site.logo_url.startsWith("http") ? branding.site.logo_url : `${process.env.REACT_APP_BACKEND_URL}${branding.site.logo_url}`)
    : null;
  const fhAddress = record?.funeral_home?.business_address
    ? [record.funeral_home.business_address.address, record.funeral_home.business_address.city, record.funeral_home.business_address.state, record.funeral_home.business_address.zipcode].filter(Boolean).join(", ")
    : "";
  const decedentName = [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ") || "—";

  // Prints via the browser's own Print → Save as PDF, rather than a client-side PDF
  // library — see .it-case-print below for the stylesheet that makes only this dialog's
  // content printable. Browsers suggest the "Save as" filename from document.title at
  // the moment print() is called, so swap it to the case name for that one call. Always
  // the CASE number, never one order's — the printed document combines every order on
  // the case, so titling it after whichever order happened to be focused when the
  // dialog opened would misdescribe the rest of the document.
  const handleDownloadPdf = () => {
    const name = record?.case_number ? `Case ${record.case_number}` : "Case";
    const previousTitle = document.title;
    document.title = name;
    window.print();
    document.title = previousTitle;
  };

  return (
    <>
    <style>{`
      @media print {
        body * { visibility: hidden; }
        .it-case-print, .it-case-print * { visibility: visible; }
        .it-case-print {
          position: absolute !important;
          inset: 0 !important;
          transform: none !important;
          max-height: none !important;
          overflow: visible !important;
          width: 100% !important;
          box-shadow: none !important;
          border: none !important;
        }
        /* Chrome/Safari/Firefox all drop background colors on print by default
           unless told to keep them — without this the banner and table header
           bars below print as plain white regardless of their Tailwind bg-*
           class, even though they render correctly on screen. */
        .it-case-print, .it-case-print * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        /* Dialog's built-in close (X) button — dialog.jsx renders it right after
           this component's own children, so it's always the last direct child. */
        .it-case-print > button:last-of-type { display: none !important; }
        @page { margin: 1.5cm; }
      }
    `}</style>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto it-case-print">
        <DialogHeader className="print:hidden">
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Case {record?.case_number || ""}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-5 print:hidden">
            {/* Status + email ordering, first thing in the popup. The legacy admin put this
                button directly under the status line and that is where staff look for it;
                below the case images it sits under the fold of a scrolling modal. */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-slate-50/60 px-4 py-3">
              <p className="text-sm text-slate-600">
                Status:{" "}
                <span className={`font-semibold ${record?.status === "ordered" ? "text-green-700" : "text-amber-600"}`}>
                  {record?.status === "ordered" ? "Print Order" : "Order Saved"}
                </span>
                {emailOrders.length > 0 && (
                  // A count, not a status. With several email orders on a case, naming
                  // one order's status here would misreport the others; each card below
                  // carries its own stage badge and timeline, which is where TR-30 1.2's
                  // "the admin must see the status change stick" is now answered.
                  <span className="ml-2 text-xs text-amber-700">
                    · {emailOrders.length} email order{emailOrders.length === 1 ? "" : "s"}
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="print:hidden" onClick={handleDownloadPdf}>
                  <Download className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
                {canEmailOrder && (
                  <Button
                    size="sm"
                    className="print:hidden bg-green-600 hover:bg-green-700"
                    onClick={() => setEmailOrderDialog({ open: true, order: null })}
                    title="Record another order taken over email or phone against this case"
                  >
                    <Mail className="w-4 h-4 mr-1.5" /> New Email Order
                  </Button>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Case / deceased details */}
              <Section title="Case Details">
                <div className="grid gap-x-6 gap-y-1 text-sm">
                  <KV k="Case Number" v={record?.case_number || "—"} />
                  {/* Unique, unlike case_number, which the funeral home types itself —
                      this is the reference the family login link carries. */}
                  <KV k="Case Code" v={record?.case_code || "—"} />
                  <KV k="Name" v={[s.first_name, s.middle_name, s.last_name].filter(Boolean).join(" ") || "—"} />
                  <KV k="Type" v={s.subject_type === "pet" ? "Pet" : "Human"} />
                  <KV k="Gender" v={s.gender || "—"} />
                  <KV k="Print Type" v={record?.print_type || "—"} />
                  <KV k="Date of Birth" v={fmtDate(s.date_of_birth)} />
                  <KV k="Date of Death" v={fmtDate(s.date_of_death)} />
                  <KV k="Time of Death" v={s.time_of_death || "—"} />
                  <KV k="Place of Death" v={s.place_of_death || "—"} />
                </div>
              </Section>

              {/* Funeral home (who created the case) */}
              <Section title="Funeral Home">
                {record?.funeral_home ? (
                  <div className="grid gap-x-6 gap-y-1 text-sm">
                    <KV k="Business" v={record.funeral_home.business_name || "—"} />
                    <KV k="Email" v={record.funeral_home.contact_email || "—"} />
                    <KV k="Phone" v={record.funeral_home.contact_phone || "—"} />
                    <KV k="Subdomain" v={record.funeral_home.subdomain || "—"} />
                    {record.funeral_home.business_address && (
                      <KV k="Address" v={[record.funeral_home.business_address.address, record.funeral_home.business_address.city, record.funeral_home.business_address.state, record.funeral_home.business_address.zipcode].filter(Boolean).join(", ") || "—"} />
                    )}
                  </div>
                ) : <p className="text-sm text-slate-400">—</p>}
              </Section>
            </div>

            {/* Case images — separate section */}
            <Section title="Case Images (Prints)">
              {(record?.captures || []).length === 0 ? (
                <p className="text-sm text-slate-400">No print images captured.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {record.captures.map((cap, i) => {
                    const url = cap.image_url ? (cap.image_url.startsWith("http") ? cap.image_url : `${process.env.REACT_APP_BACKEND_URL}${cap.image_url}`) : null;
                    return url ? (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" download className="text-center text-[10px] text-blue-700">
                        <img src={getImageUrl(cap.image_url)} alt="" className="w-20 h-20 object-cover rounded-lg border" />
                        <div className="mt-0.5">{describeTarget(cap.target)} ↓</div>
                      </a>
                    ) : (
                      <div key={i} className="w-20 h-20 rounded-lg border bg-slate-50 flex items-center justify-center text-[10px] text-slate-300">no image</div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Customer link */}
            <Section title="Customer Link">
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 break-all hover:underline">{link}</a>
              ) : (
                <p className="text-xs text-slate-500">—</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Customer PIN: <span className="font-bold text-slate-800">{linkPin || "—"}</span></p>
            </Section>

            {/* 1.3 Orders against this case */}
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Orders ({orders.length})</h4>
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.order_id} ref={(el) => (sectionRefs.current[o.order_id] = el)}
                    className={`border rounded-xl p-4 ${o.order_id === focusOrderId ? "border-blue-300 ring-2 ring-blue-100" : "border-slate-200"}`}>
                    <OrderSection
                      order={o}
                      onUpdated={load}
                      apiBase={apiBase}
                      readOnly={readOnly}
                      allowEmailOrders={allowEmailOrders}
                      onUpdateEmailOrder={(order) => setEmailOrderDialog({ open: true, order })}
                    />
                  </div>
                ))}
                {orders.length === 0 && <p className="text-sm text-slate-400">No orders placed against this case yet.</p>}
              </div>
            </div>
          </div>
        )}

        {/* ============ Print-only branded document — invisible on screen, shown only
            when printing/saving as PDF (see .it-case-print above). Separate markup
            from the on-screen dialog body above (hidden via print:hidden) rather than
            reusing it directly, since the branded layout groups/labels content
            differently (e.g. Funeral Home info under "Identification", larger
            fingerprint images) than the interactive admin view does. */}
        {!loading && (
          <div className="hidden print:block" style={{ fontFamily: "'Inter', Helvetica, Arial, sans-serif" }}>
            <div className="bg-blue-700 text-white text-center px-6 py-6 -mx-6 -mt-6">
              <div className="text-2xl font-bold tracking-wide uppercase">{brandName}</div>
              {brandTagline && <div className="text-sm font-semibold mt-1">{brandTagline}</div>}
              {branding?.seo?.description && <div className="text-xs mt-2 opacity-90 max-w-xl mx-auto">{branding.seo.description}</div>}
            </div>

            <div className="flex items-center justify-between border-b py-3 px-1">
              <div className="flex items-center gap-2">
                {brandLogoUrl && !logoFailed ? (
                  <img
                    src={brandLogoUrl}
                    alt={brandName}
                    className="h-14 w-auto max-w-[160px] object-contain"
                    // The CMS's stored logo can 404 (e.g. an upload missing from this
                    // environment) — fall back to the site name as text instead of a
                    // broken-image icon.
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <span className="text-base font-semibold text-blue-700">{brandName}</span>
                )}
              </div>
              <div className="text-xs">
                <span className="font-bold">Email:</span> {record?.funeral_home?.contact_email || "—"}
              </div>
              <div className="text-right">
                <div className="text-xs"><span className="font-bold">Case:</span> {record?.case_number || "—"}</div>
                <div className="text-sm font-bold text-blue-700">CASE DETAILS</div>
              </div>
            </div>

            <PrintTable title="Decedent Info" pairs={[
              ["Name", decedentName],
              ["Gender", s.gender || "—"],
              ["DOB", fmtDate(s.date_of_birth)],
              ["DOD", fmtDate(s.date_of_death)],
              ["Place Of Death", s.place_of_death || "—"],
              ["Record Created", fmtDate(record?.created_at)],
            ]} />

            <PrintTable title="Identification" pairs={[
              ["Business Name", record?.funeral_home?.business_name || "—"],
              ["Email", record?.funeral_home?.contact_email || "—"],
              ["Phone", record?.funeral_home?.contact_phone || "—"],
              ["Address", fhAddress || "—"],
            ]} />

            <div className="mb-4" style={{ breakInside: "avoid" }}>
              <div className="bg-slate-100 border border-b-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-700">Fingerprints</div>
              <div className="border p-4 flex flex-wrap gap-4">
                {(record?.captures || []).length === 0 ? (
                  <span className="text-xs text-slate-400">No print images captured.</span>
                ) : (
                  record.captures.map((cap, i) => {
                    const url = cap.image_url ? (cap.image_url.startsWith("http") ? cap.image_url : `${process.env.REACT_APP_BACKEND_URL}${cap.image_url}`) : null;
                    return url ? (
                      <div key={i} className="text-center">
                        <img src={url} alt="" className="w-56 h-auto border rounded" />
                        <div className="text-xs mt-1 text-slate-600">{describeTarget(cap.target)}</div>
                      </div>
                    ) : null;
                  })
                )}
              </div>
            </div>

            {/* Full-width rows rather than PrintTable's two-per-row grid — the login URL
                is too long to read in a half-width cell. Kept an <a> so the link stays
                clickable in the saved PDF. */}
            <div className="mb-4" style={{ breakInside: "avoid" }}>
              <div className="bg-slate-100 border border-b-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-700">Customer Link</div>
              <div className="border">
                <div className="flex text-xs border-b">
                  <div className="w-32 shrink-0 bg-slate-50 px-3 py-2 font-semibold text-slate-600 border-r">Link</div>
                  <div className="px-3 py-2 flex-1 break-all">
                    {link ? <a href={link} className="text-blue-700">{link}</a> : "—"}
                  </div>
                </div>
                <div className="flex text-xs">
                  <div className="w-32 shrink-0 bg-slate-50 px-3 py-2 font-semibold text-slate-600 border-r">Customer PIN</div>
                  <div className="px-3 py-2 flex-1 font-bold text-slate-900">{linkPin || "—"}</div>
                </div>
              </div>
            </div>

            <div className="text-sm font-bold text-blue-700 mt-5 mb-2">Orders ({orders.length})</div>
            {orders.map((o) => <PrintOrderBlock key={o.order_id} order={o} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Sibling, not nested inside the case dialog: two Radix roots side by side layer
        and restore focus predictably. `load` refetches the case so the new/updated
        email order and its remark appear immediately. */}
    <EmailOrderDialog
      open={emailOrderDialog.open}
      // Only `open` flips on close: clearing `order` here would retitle the dialog
      // "New Email Order" mid close-animation. Every open sets its own target.
      onClose={() => setEmailOrderDialog((d) => ({ ...d, open: false }))}
      memorialRecordId={memorialRecordId}
      order={emailOrderDialog.order}
      onSaved={load}
    />
    </>
  );
}

function OrderSection({ order, onUpdated, apiBase = "admin", readOnly = false, allowEmailOrders = true, onUpdateEmailOrder }) {
  const isEmailOrder = Boolean(order.is_email_order);
  const [status, setStatus] = useState(order.status || "pending");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // The row re-renders from a refetched order after every save, so the control has to
  // follow the order rather than keep its first value — otherwise it snaps back to the
  // old status and invites the admin to re-submit it.
  useEffect(() => {
    setStatus(order.status || "pending");
  }, [order.status]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/${apiBase}/orders/${order.order_id}/status`, { status, notes: note });
      toast.success(`Order ${order.order_number} updated`);
      setNote("");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const history = order.status_history || [];

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{order.order_number}</p>
          <p className="text-xs text-slate-500">
            {fmt(order.created_at)} · {money(order.total_charged || order.total)} · Placed by {order.placed_by === "funeral_home" ? "Funeral Home" : "Family"}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {order.is_email_order && (
            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Email Order</Badge>
          )}
          <PaymentStatusBadge status={order.payment_status} />
          <OrderStageBadge order={order} />
        </div>
      </div>

      {/* Buyer + shipping address, per order (TR-23 #2). A case can carry orders from
          several different family members, so this must never be read off the case:
          each order stores the details of whoever actually paid for it. Without this
          block an admin has no address to ship to. */}
      <div className="grid sm:grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Ordered by</p>
          <p className="text-slate-800">{order.customer_name || "—"}</p>
          {order.customer_email || order.contact_email ? (
            <p className="text-xs text-slate-500 break-all">{order.customer_email || order.contact_email}</p>
          ) : null}
          {order.shipping_address?.phone ? <p className="text-xs text-slate-500">{order.shipping_address.phone}</p> : null}
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 mb-0.5">Ship to</p>
          {order.shipping_address ? (
            <>
              <p className="text-slate-800">{order.shipping_address.name || order.customer_name || "—"}</p>
              <p className="text-xs text-slate-500">{addrLine(order.shipping_address) || "—"}</p>
            </>
          ) : (
            <p className="text-xs text-slate-400">No address on this order</p>
          )}
          {order.billing_address && addrLine(order.billing_address) !== addrLine(order.shipping_address) ? (
            <p className="text-xs text-slate-400 mt-1">Billing: {addrLine(order.billing_address)}</p>
          ) : null}
        </div>
      </div>

      {/* items with product images */}
      <div className="space-y-2">
        {(order.items || []).map((it, i) => {
          const img = it.image_url ? (it.image_url.startsWith("http") ? it.image_url : getImageUrl(it.image_url)) : null;
          return (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-12 h-12 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {img ? <img src={img} alt={it.name} className="w-full h-full object-contain" /> : <span className="text-[9px] text-slate-300">no image</span>}
              </div>
              <div className="min-w-0">
                <p className="text-slate-800 font-medium">{it.name} × {it.quantity} — {money(it.display_price)}</p>
                {(it.engraving_lines || []).length > 0 && <p className="text-xs text-slate-400">Engraving: {it.engraving_lines.join(" / ")}</p>}
                {it.ash_chamber && <p className="text-xs text-slate-400">+ Ash chamber</p>}
                {/* The print chosen for this line, when the buyer picked one. Opens the
                    full-size image in a new tab, same as the Case Images section above. */}
                {it.custom_image_url ? (
                  <a
                    href={getImageUrl(it.custom_image_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline"
                  >
                    <img src={getImageUrl(it.custom_image_url)} alt="Selected print" className="w-10 h-10 object-cover rounded border" />
                    {/* Deliberately generic: the same link carries paw, nose and
                        signature prints too, not only fingerprints (TR-22). */}
                    <span>View Images ↗</span>
                  </a>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {history.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-2 space-y-1">
          {history.map((h, i) => (
            <div key={i} className="text-[11px] text-slate-500 flex items-start gap-1">
              <Clock className="w-3 h-3 mt-0.5" /> <span className="font-medium capitalize">{h.status}</span>
              {h.note ? ` — ${h.note}` : ""} <span className="text-slate-400">· {fmt(h.at)} ({h.changed_by})</span>
            </div>
          ))}
        </div>
      )}

      {/* Status change — admin only; hidden in read-only (FH) mode.
          An email order gets its own button instead of the inline row: it moves through
          two rungs only, and the popup is where its status and remark are chosen. Each
          card carries its own button, scoped to that order — the case-level green button
          now only ever opens a NEW email order, so recording a second phoned-in order no
          longer overwrites the first. */}
      {!readOnly && (isEmailOrder ? (allowEmailOrders && (
        <div className="print:hidden flex justify-end border-t pt-3">
          <Button size="sm" variant="outline" onClick={() => onUpdateEmailOrder?.(order)}>
            <Mail className="w-4 h-4 mr-1.5" /> Update Status
          </Button>
        </div>
      )) : (
        <div className="print:hidden flex flex-wrap items-end gap-2 border-t pt-3">
          <div className="w-40">
            <label className="text-[10px] text-slate-400 uppercase font-bold">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTS.map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-h-[40px] h-10" />
          <Button size="sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Update"}</Button>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-slate-50/60 border rounded-xl p-4">
      <h4 className="font-semibold text-slate-800 mb-2">{title}</h4>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 border-b border-slate-100 py-0.5">
      <span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span>
    </div>
  );
}

// Print-only: a gray-bar-headed, bordered 2-column key/value table — matches the
// reference document's DECEDENT INFO / IDENTIFICATION section style. `pairs` is a
// flat [label, value] list, laid out two per row (padded with a blank cell if odd).
function PrintTable({ title, pairs }) {
  const cells = pairs.length % 2 === 0 ? pairs : [...pairs, ["", ""]];
  return (
    <div className="mb-4" style={{ breakInside: "avoid" }}>
      <div className="bg-slate-100 border border-b-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-slate-700">{title}</div>
      <div className="border grid grid-cols-2">
        {cells.map(([label, value], i) => (
          <div
            key={i}
            className={`flex text-xs ${i % 2 === 0 ? "border-r" : ""} ${i < cells.length - 2 ? "border-b" : ""}`}
          >
            <div className="w-32 shrink-0 bg-slate-50 px-3 py-2 font-semibold text-slate-600 border-r">{label}</div>
            <div className="px-3 py-2 text-slate-900 flex-1 break-words">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Print-only: one order's block in the branded document — same PrintTable style for
// its own details, plus items and status history below, restyled from OrderSection's
// on-screen markup rather than reusing it (different visual language, same data).
function PrintOrderBlock({ order }) {
  const shipTo = order.shipping_address
    ? `${order.shipping_address.name || order.customer_name || "—"}, ${addrLine(order.shipping_address) || "—"}`
    : "No address on this order";

  return (
    <div className="mb-5">
      <PrintTable title={`Order ${order.order_number}`} pairs={[
        ["Date", fmt(order.created_at)],
        ["Total", money(order.total_charged || order.total)],
        ["Placed By", order.placed_by === "funeral_home" ? "Funeral Home" : "Family"],
        ["Status", order.status],
        ["Buyer", order.customer_name || "—"],
        ["Buyer Email", order.customer_email || order.contact_email || "—"],
        ["Ship To", shipTo],
      ]} />

      {(order.items || []).length > 0 && (
        <div className="border border-t-0 px-3 py-2 text-xs space-y-1" style={{ breakInside: "avoid" }}>
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between gap-3">
              <span>
                {it.name} × {it.quantity}
                {(it.engraving_lines || []).length > 0 ? ` — Engraving: ${it.engraving_lines.join(" / ")}` : ""}
                {it.ash_chamber ? " — Ash chamber" : ""}
              </span>
              <span className="whitespace-nowrap">{money(it.display_price)}</span>
            </div>
          ))}
        </div>
      )}

      {(order.status_history || []).length > 0 && (
        <div className="border border-t-0 px-3 py-2 text-[10px] text-slate-500 space-y-0.5">
          {order.status_history.map((h, i) => (
            <div key={i}>
              <span className="font-medium capitalize">{h.status}</span>
              {h.note ? ` — ${h.note}` : ""} · {fmt(h.at)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
